import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import RackNumberModel from './RackNumberModel';
import { showSuccess, showError } from '../../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface RackNumbers {
  rack_id: number;
  rack_name: string;
  rack_number: string;
  rack_status: number;
  }

const RackNumbers: React.FC = () => {
  const [RackNumbers, setRackNumbers] = useState<RackNumbers[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<RackNumbers | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});


  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchRackNumbers = async () => {
    try {
      const data = await apiRequest<RackNumbers[]>({
        method: 'get',
        url: 'rack',
      });
      setRackNumbers(data);
    } catch (error) {
      showError('Failed to fetch RackNumbers');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchRackNumbers();
  }, []);

  const handleEditClick = (record: RackNumbers) => {
    setIsEditMode(true);
    setInitialValues({
      rack_id: record.rack_id,
      rack_name: record.rack_name,
      rack_number: record.rack_number,
      rack_status: record.rack_status,
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchRackNumbers();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (rack_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-rack/${rack_id}`,
      });
      showSuccess('rack deleted');
      fetchRackNumbers();
    } catch {
      showError('Failed to delete rack');
    }
  };

  const handleStatusToggle = async (RackNumberId: number) => {
    setRackNumbers((prev) =>
      prev.map((item) =>
        item.rack_id === RackNumberId
          ? { ...item, rack_status: item.rack_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-rack/${RackNumberId}`,
      });
      showSuccess('rack status updated');
    } catch (error) {
      showError('Failed to update status');
      setRackNumbers((prev) =>
        prev.map((item) =>
          item.rack_id === RackNumberId
            ? { ...item, rack_status: item.rack_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<RackNumbers>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<RackNumbers> = [
    {
      title: 'Rack Name',
      dataIndex: 'rack_name',
      key: 'rack_name',
      filters: Array.from(new Set(RackNumbers.map((s) => s.rack_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.rack_name || null,
      onFilter: (value, record) => record.rack_name.includes(value as string),
      sorter: (a, b) => a.rack_name.localeCompare(b.rack_name),
      sortOrder: sortedInfo.columnKey === 'rack_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.rack_name}</div>,
    },
    {
      title: 'Rack Number',
      dataIndex: 'rack_number',
      key: 'rack_number',
      filters: Array.from(new Set(RackNumbers.map((s) => s.rack_number))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.rack_number || null,
      onFilter: (value, record) => record.rack_number.includes(value as string),
      sorter: (a, b) => a.rack_number.localeCompare(b.rack_number),
      sortOrder: sortedInfo.columnKey === 'rack_number' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
      render: (_, record) => <div>{record.rack_number}</div>,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_: any, record: RackNumbers) => (
        <Space size="middle">
          {currentModule?.right_edit === 'Y' && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.rack_status}
                onChange={() => handleStatusToggle(record.rack_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === 'Y' && (
            <Popconfirm
              title="Delete this rack number?"
              onConfirm={() => handleDelete(record.rack_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Rack Numbers</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Rack Number
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<RackNumbers>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="rack_id"
          dataSource={RackNumbers.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 11 }}
          locale={{ emptyText: 'No rack number found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, RackNumbers.length)} of{' '}
                {RackNumbers.length}
              </span>
              <Pagination
                {...pagination}
                total={RackNumbers.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <RackNumberModel
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default RackNumbers;